import React, { memo, useContext } from 'react';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import T from '@prisjakt/ui-translate';
import { AppContext } from '@prisjakt/ui-app-context';
import PriceLabel, { priceLabel } from '@prisjakt/ui-price-label';
import Currency, { formatCurrency } from '@prisjakt/ui-currency';
import { Link } from 'react-router-dom';
import Graph from 'src/components/graph';
import PriceHistoryCurve from 'src/components/graph/components/price-history-curve';
import { IGraphRenderProps, IPriceHistoryLoaderProps } from './types';
import { formatDate } from './utils';
import { Footer } from './styles';
import { Maybe } from 'src/types';

const PriceHistoryLoader: React.FC<IPriceHistoryLoaderProps> = props => {
  const { t, market } = useContext(AppContext);
  const { statistics } = props;
  if (!statistics.pageInfo || !statistics.nodes || !statistics.nodes.length) {
    return null;
  }

  const { lowestPrice } = statistics.pageInfo;

  let defaultGraphs;
  let defaultXScaleData: Date[] | undefined;
  let defaultYScaleData: (Maybe<number> | undefined)[] | undefined;

  if (!props.graphs) {
    defaultGraphs = [
      {
        name: 'price-history',
        legend: {
          label: t('product', 'price-history.actual-price'),
          color: '#4895B6',
          lineType: 'normal',
        },
        render: (graphProps: IGraphRenderProps) => (
          <PriceHistoryCurve
            key={graphProps.name}
            data={statistics.nodes}
            {...graphProps}
          />
        ),
      },
    ];

    defaultXScaleData = statistics.nodes.map(x => new Date(x!.date!));
    defaultYScaleData = statistics.nodes.map(x => x!.lowestPrice);
  }

  return (
    <div>
      <Panel
        header={
          props.withTitle &&
          props.route && (
            <PanelHeader
              title={t('product', 'price-history.title')}
              margin="bottom"
            >
              <Link to={props.route}>{t('common', 'button.show-more')}</Link>
            </PanelHeader>
          )
        }
        footer={
          market.showProductInformationTexts &&
          props.productName && (
            <Footer>
              <T domain="product" productName={props.productName}>
                price-history.data-information
              </T>
            </Footer>
          )
        }
      >
        {props.withSummary && lowestPrice && (
          <div>
            <T
              domain="product"
              price={
                <PriceLabel
                  key="price-label"
                  styling={priceLabel.SECONDARY}
                  bold
                  value={lowestPrice.price}
                />
              }
              date={lowestPrice.date}
            >
              price-history.description
            </T>
          </div>
        )}

        {IS_BROWSER && (
          <Graph
            id="pricehistory"
            responsive
            showXGrid={false}
            xScaleData={defaultXScaleData || props.xScaleData || []}
            yScaleData={defaultYScaleData || props.yScaleData || []}
            xTickFormat={formatDate}
            yTickFormat={(value: number) => {
              return (
                formatCurrency(value, market.code, false, market.currency!) ??
                ''
              );
            }}
            renderTooltip={toolTipProps => (
              <>
                <div style={{ fontWeight: 'bold', marginBottom: 5 }}>
                  {toolTipProps.xValue}
                </div>
                <div>
                  <Currency price={toolTipProps.yValue} market={market.code} />
                </div>
                {toolTipProps.importantDates &&
                  toolTipProps.importantDates.length > 0 && (
                    <div style={{ marginTop: 5, whiteSpace: 'pre-wrap' }}>
                      {toolTipProps.importantDates
                        .map(date => date?.name)
                        .join('\n')}
                    </div>
                  )}
              </>
            )}
            storePrices={
              props.selectedPrices &&
              Object.keys(props.selectedPrices)
                .filter(
                  id => props.priceHistory[id] && props.priceHistory[id].prices
                )
                .map(id => ({
                  id,
                  prices: props.priceHistory[id].prices,
                }))
            }
            storePriceColors={props.selectedPrices}
            nodes={[
              statistics.nodes.map(item => ({
                date: item?.date,
                value: item?.lowestPrice,
              })),
            ]}
            graphs={defaultGraphs || props.graphs || []}
            aspectRatio={props.graphAspectRatio}
            legend={props.legend}
          />
        )}
      </Panel>
    </div>
  );
};

PriceHistoryLoader.defaultProps = {
  withTitle: true,
  withSummary: true,
  selectedPrices: {},
  priceHistory: {},
  xScaleData: [],
  yScaleData: [],
};

export default memo(PriceHistoryLoader);
